import type { EtapaComercial, StatusReserva } from './statusReserva'

/**
 * Score Comercial (PDF Pipeline §2.1, decisão 8.1 de 28/09) — espelho de
 * `dto/ScoreComercialResponse`.
 *
 * É DERIVADO da etapa pela tabela versionada `ScoreEtapa` do backend: nunca digitado,
 * nunca calculado aqui. A tela mostra o que recebe — dimensões, total, classe e a
 * versão da tabela — e nada mais. Se o backend não mandar, a tela diz "indisponível";
 * nunca inventa um número.
 */
export type ClasseScore = 'BAIXA' | 'MEDIA' | 'ALTA'

export interface ScoreComercial {
  bant: number
  apgar: number
  impacto: number
  total: number
  classe: ClasseScore
  /** Versão da tabela usada (ex.: `score-etapa.2026-09-28.v1`). */
  versao: string
}

export const CLASSE_SCORE_LABEL: Record<ClasseScore, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
}

export const CLASSES_SCORE: ClasseScore[] = ['BAIXA', 'MEDIA', 'ALTA']

/** Cor semântica do chip de classe (tema MUI): Alta = success, Média = warning, Baixa = neutra. */
export const classeScoreChipColor = (c: ClasseScore): 'default' | 'warning' | 'success' => {
  switch (c) {
    case 'ALTA':
      return 'success'
    case 'MEDIA':
      return 'warning'
    default:
      return 'default'
  }
}

/**
 * Uma linha do histórico real de etapas — espelho de `dto/HistoricoEtapaResponse`
 * (`GET /v1/oportunidades/{id}/historico-etapas`). É o snapshot gravado na transição
 * (V47): status e etapa anterior/nova, score anterior/novo e a versão da tabela.
 */
export interface HistoricoEtapa {
  id: string
  statusAnterior?: StatusReserva | null
  statusNovo?: StatusReserva | null
  etapaAnterior?: EtapaComercial | null
  etapaNova?: EtapaComercial | null
  scoreAnterior?: number | null
  scoreNovo?: number | null
  versaoScore?: string | null
  /** Presente só quando a transição levou a Perdido. */
  motivoPerdaCodigo?: string | null
  usuarioId?: string | null
  createdAt: string
  descricao?: string | null
}

/**
 * Item do catálogo de motivos de perda (decisão 8.6) — espelho de `dto/MotivoPerdaResponse`.
 * `GET /v1/oportunidades/motivos-perda` devolve só os ativos e selecionáveis.
 */
export interface MotivoPerda {
  codigo: string
  rotulo: string
  ativo: boolean
  /** `false` = automático (reprovação): o servidor preenche, o usuário não escolhe. */
  selecionavel: boolean
  exigeObservacao: boolean
  ordem: number
}

/** O que o diálogo de perda entrega para o PATCH de status. */
export interface DadosPerda {
  motivoPerdaCodigo: string
  observacao?: string
}

/** Códigos de erro do backend que o diálogo de perda sabe apontar no campo certo. */
export type ErroMotivoPerda = 'MOTIVO_PERDA_OBRIGATORIO' | 'MOTIVO_PERDA_OBSERVACAO_OBRIGATORIA'

/** Código do 409 de controle otimista (PDF §2.4) — a reserva mudou desde a leitura. */
export const CONFLITO_VERSAO = 'CONFLITO_VERSAO'

export const MSG_CONFLITO_VERSAO =
  'Esta reserva foi alterada por outra pessoa. Os dados foram atualizados — tente novamente.'

/**
 * Uma etapa comercial no `/v1/dashboard/resumo` — espelho de
 * `DashboardResumoResponse.EtapaComercialResumo`. `classe`/`scoreTotal` vêm da tabela do
 * backend (uma classe por etapa, porque o score é derivado da etapa); `porClasse` traz as
 * três chaves para o KPI somar contagens sem conhecer a tabela; `aderenciaMedia` é nula em
 * etapa vazia (nunca "0" inventado).
 */
export interface EtapaComercialResumo {
  classe: ClasseScore
  scoreTotal: number
  versaoScore: string
  porClasse: Record<ClasseScore, number>
  aderenciaMedia?: number | null
}
